import { useEffect, useState } from 'react'

import {
  Badge,
  Box,
  Card,
  Collapse,
  Flex,
  Grid,
  Group,
  Loader,
  Stack,
  Text,
} from '@mantine/core'
import {
  IconChevronDown,
  IconChevronRight,
  IconExternalLink,
} from '@tabler/icons-react'
import { upperFirst } from 'lodash-es'

import { TowerCard } from '@/components/ui/TowerCard'
import type { AccountLeaf, AccountNode } from '@/hooks/useAccountLookups.ts'

import styles from './AccountsTree.module.css'

const enableLines = false // they don't look good yet

export interface AccountsTreeProps {
  data: AccountNode[]
  mode?: 'tree' | 'cards'
  onNodeClick?: (node: AccountNode) => void
  onModeChange?: (mode: 'tree' | 'cards') => void
  isFetching?: boolean
}

export const AccountsTree: React.FC<AccountsTreeProps> = ({
  data,
  onNodeClick,
  mode = 'tree',
  isFetching,
}) => {
  // const modeSwitcher = <Group>
  //   <Text size="sm" fw={700} c="dimmed">
  //     View Mode:
  //   </Text>
  //   <Group gap={0}>
  //     <Text
  //       size="sm"
  //       fw={700}
  //       c={internalMode === 'tree' ? 'blue' : 'dimmed'}
  //       onClick={() => handleModeChange('tree')}
  //       style={{ cursor: 'pointer' }}
  //     >
  //       Tree
  //     </Text>
  //     <Text size="sm" fw={700} c="dimmed" mx={5}>
  //       /
  //     </Text>
  //     <Text
  //       size="sm"
  //       fw={700}
  //       c={internalMode === 'cards' ? 'blue' : 'dimmed'}
  //       onClick={() => handleModeChange('cards')}
  //       style={{ cursor: 'pointer' }}
  //     >
  //       Cards
  //     </Text>
  //   </Group>
  // </Group>

  const [showLoader, setShowLoader] = useState(false)

  useEffect(() => {
    if (isFetching) {
      setShowLoader(true)
    } else {
      const timeout = setTimeout(() => {
        setShowLoader(false)
      }, 500)
      return () => {
        clearTimeout(timeout)
      }
    }
  }, [isFetching])

  const loader = showLoader && (
    <Box
      style={{
        opacity: isFetching ? 1 : 0,
        transition: 'opacity .5s ease-out',
      }}
    >
      <Loader size="sm" />
    </Box>
  )

  return (
    <div className={styles.accounts_tree}>
      {mode === 'tree' ? (
        <TowerCard
          title="Live Accounts"
          className={styles.tree_mode}
          extraHeader={loader}
        >
          <TreeNodeHeader />
          {data.map((node, index) => {
            const isLast = index === data.length - 1
            return (
              <TreeNode
                key={node.id}
                node={node}
                depth={0}
                onNodeClick={onNodeClick}
                isLastChild={isLast}
                parentLinePositions={[]}
              />
            )
          })}
        </TowerCard>
      ) : (
        <Grid gutter="md">
          {data.map((node) => (
            <Grid.Col
              key={node.id}
              span={node.type === 'container' ? 12 : { base: 12, sm: 6 }}
            >
              <CardNode node={node} onNodeClick={onNodeClick} level={0} />
            </Grid.Col>
          ))}
        </Grid>
      )}
    </div>
  )
}

const columnWidths = [4, 2, 3, 2, 1]

const TreeNodeHeader: React.FC = () => {
  return (
    <Box className={styles.tree_header}>
      <Grid className={styles.tree_header_grid}>
        <Grid.Col span={columnWidths[0]}>
          <Text size="sm" fw={700}>
            Name
          </Text>
        </Grid.Col>
        <Grid.Col span={columnWidths[1]}>
          <Text size="sm" fw={700}>
            Provider
          </Text>
        </Grid.Col>
        <Grid.Col span={columnWidths[2]}>
          <Text size="sm" fw={700}>
            Balances
          </Text>
        </Grid.Col>
        <Grid.Col span={columnWidths[3]}>
          <Text size="sm" fw={700}>
            USD Value
          </Text>
        </Grid.Col>
        <Grid.Col span={columnWidths[4]}>
          <Text size="sm" fw={700}></Text>
        </Grid.Col>
      </Grid>
    </Box>
  )
}

interface TreeNodeProps {
  node: AccountNode
  depth: number
  onNodeClick?: (node: AccountNode) => void
  isLastChild?: boolean
  parentLinePositions?: number[]
}

const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  depth,
  onNodeClick,
  isLastChild = false,
  parentLinePositions = [],
}) => {
  // Check if this is a bottom-layer container (only contains leaf nodes)
  const isBottomLayerContainer =
    node.type === 'container' &&
    node.children.every((child) => child.type === 'leaf')

  const [isExpanded, setIsExpanded] = useState(!isBottomLayerContainer)

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (node.type === 'container') {
      setIsExpanded(!isExpanded)
    }
  }

  const handleClick = () => {
    if (node.type === 'container') {
      setIsExpanded(!isExpanded)
    }
    onNodeClick?.(node)
  }

  const childLinePositions = [...parentLinePositions]
  if (depth > 0 && !isLastChild) {
    childLinePositions.push(depth - 1)
  }

  const isZero = (n: AccountNode) =>
    (n.type === 'leaf' && n.balance === 0) ||
    (n.type === 'container' &&
      Object.entries(n.assetSums || {}).every(([_, sum]) => sum === 0))

  const renderTreeLines = () => {
    if (depth === 0) return null
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!enableLines) return null

    return (
      <div className={styles.tree_line_container}>
        {Array.from({ length: depth }).map((_, index) => (
          <div key={`indent-${index}`} className={styles.indent_guide}>
            {index < depth - 1 && parentLinePositions.includes(index) && (
              <div className={styles.vertical_line} />
            )}
            {index === depth - 1 && (
              <>
                <div className={styles.vertical_line} />
                <div className={styles.horizontal_line} />
              </>
            )}
          </div>
        ))}
      </div>
    )
  }

  const renderLeaf = () => {
    if (node.type != 'leaf') return null
    return (
      <>
        <Grid.Col span={columnWidths[1]}></Grid.Col>
        <Grid.Col span={columnWidths[2]}>
          {isZero(node) ? (
            <Text fw={500} size="sm" ff="monospace" c="dimmed">
              0
            </Text>
          ) : (
            <Text fw={500} size="sm" ff="monospace">
              {node.assetSymbol}: {node.balance.toLocaleString()}
            </Text>
          )}
        </Grid.Col>
        <Grid.Col span={columnWidths[3]}>
          {node.usdEquivalent ? (
            <Flex direction="column">
              <Text fw={600} size="sm" ff="monospace">
                ${node.usdEquivalent.toLocaleString()}
              </Text>
              {node.balance > 0 && node.usdEquivalent / node.balance != 1 && (
                <Text size="xs" ff="monospace" c="dimmed">
                  Price: ${(node.usdEquivalent / node.balance).toFixed(2)}
                </Text>
              )}
            </Flex>
          ) : (
            <Text size="sm" ff="monospace" c="dimmed">
              {node.balance == 0 ? '$0' : '-'}
            </Text>
          )}
        </Grid.Col>
        <Grid.Col span={columnWidths[4]}></Grid.Col>
      </>
    )
  }

  const renderContainer = () => {
    if (node.type != 'container') return null
    return (
      <>
        <Grid.Col span={columnWidths[1]}>
          {node.provider && (
            <Text size="sm" c="dimmed">
              {upperFirst(node.provider)}
            </Text>
          )}
        </Grid.Col>
        <Grid.Col span={columnWidths[2]}>
          <Stack gap={0}>
            {isZero(node) && (
              <Text fw={500} size="sm" ff="monospace" c="dimmed">
                0
              </Text>
            )}
            {node.assetSums &&
              Object.entries(node.assetSums)
                .filter(([_, sum]) => sum !== 0)
                .map(([symbol, sum]) => (
                  <Text key={symbol} fw={500} size="sm" ff="monospace">
                    {symbol}: {sum.toLocaleString()}
                  </Text>
                ))}
          </Stack>
        </Grid.Col>
        <Grid.Col span={columnWidths[3]}>
          {node.totalUsdEquivalent ? (
            <Text fw={700} ff="monospace" c="blue">
              ${node.totalUsdEquivalent.toLocaleString()}
            </Text>
          ) : (
            <Text size="sm" ff="monospace" c="dimmed">
              $0
            </Text>
          )}
        </Grid.Col>
        <Grid.Col span={columnWidths[4]}>
          {node.detailsUrl && (
            <Box
              component="a"
              href={node.detailsUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation()
              }}
              className={styles.external_link_icon}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <IconExternalLink size={16} />
            </Box>
          )}
        </Grid.Col>
      </>
    )
  }

  const depthClass =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    styles[`tree_depth_${Math.min(depth, 4)}` as keyof typeof styles] ||
    styles.tree_depth_4

  // Add external class if the node is external
  const nodeClasses = `${styles.tree_node} ${depthClass} ${node.type === 'container' && node.external ? styles.external_node : ''}`

  return (
    <div className={styles.tree_node_container}>
      <div className={nodeClasses} onClick={handleClick}>
        {renderTreeLines()}
        <Grid className={styles.tree_node_grid}>
          <Grid.Col
            span={columnWidths[0]}
            style={{ paddingLeft: `${depth * 28 + 12}px` }}
          >
            <Group className={styles.tree_node_label}>
              {node.type === 'container' && (
                <Box className={styles.expand_icon} onClick={handleToggle}>
                  {isExpanded ? (
                    <IconChevronDown size={16} />
                  ) : (
                    <IconChevronRight size={16} />
                  )}
                </Box>
              )}
              <Stack gap={0}>
                <Group gap={5}>
                  <Text
                    fw={node.type === 'container' ? 700 : 500}
                    c={node.type === 'container' ? '' : 'dimmed'}
                    size={node.type === 'container' ? 'md' : 'sm'}
                  >
                    {node.name}
                  </Text>
                  {node.type === 'container' && node.external && (
                    <Text size="xs" c="orange">
                      External - Rollup Excluded
                    </Text>
                  )}
                </Group>
                {node.type === 'container' && node.description && (
                  <Text size="sm" c="dimmed">
                    {node.description}
                  </Text>
                )}
              </Stack>
            </Group>
          </Grid.Col>
          {renderLeaf()}
          {renderContainer()}
        </Grid>
      </div>

      {node.type === 'container' && (
        <Collapse in={isExpanded}>
          <div className={styles.children}>
            {node.children.map((child, index) => {
              const isLast = index === node.children.length - 1
              return (
                <TreeNode
                  key={child.id}
                  node={child}
                  depth={depth + 1}
                  onNodeClick={onNodeClick}
                  isLastChild={isLast}
                  parentLinePositions={childLinePositions}
                />
              )
            })}
          </div>
        </Collapse>
      )}
    </div>
  )
}

interface CardNodeProps {
  node: AccountNode
  onNodeClick?: (node: AccountNode) => void
  level?: number
}

const CardNode: React.FC<CardNodeProps> = ({
  node,
  onNodeClick,
  level = 0,
}) => {
  const handleClick = () => {
    onNodeClick?.(node)
  }

  if (node.type === 'leaf') {
    return <LeafCard leaf={node} onClick={handleClick} />
  }

  return (
    <Card
      className={`${styles.container_card} ${node.external ? styles.external_node : ''}`}
      withBorder
      shadow="sm"
    >
      <Card.Section p="md" className={styles.container_header}>
        <Group style={{ justifyContent: 'space-between' }}>
          <Group>
            <Text fw={700} size="lg">
              {node.name}
            </Text>
            {node.external && <Badge color="orange">External</Badge>}
          </Group>
        </Group>

        {/*{level === 0 && ({modeSwitcher})}*/}

        {node.assetSums && Object.keys(node.assetSums).length > 0 && (
          <Group style={{ marginTop: '0.75rem', gap: '0.5rem' }}>
            {Object.entries(node.assetSums).map(([symbol, sum]) => (
              <Badge key={symbol} size="md" variant="light">
                {sum === 0 ? '0' : `${sum.toLocaleString()} ${symbol}`}
              </Badge>
            ))}
          </Group>
        )}

        {node.totalUsdEquivalent && (
          <Text fw={700} c="blue" mt="1em">
            ${node.totalUsdEquivalent.toLocaleString()} USD
          </Text>
        )}
      </Card.Section>

      <Card.Section p="md">
        <Grid gutter="md">
          {node.children.map((child) => (
            <Grid.Col
              key={child.id}
              span={child.type === 'container' ? 12 : { base: 12, sm: 6 }}
            >
              <CardNode
                node={child}
                onNodeClick={onNodeClick}
                level={level + 1}
              />
            </Grid.Col>
          ))}
        </Grid>
      </Card.Section>
    </Card>
  )
}

interface LeafCardProps {
  leaf: AccountLeaf
  onClick?: () => void
}

const LeafCard: React.FC<LeafCardProps> = ({ leaf, onClick }) => {
  return (
    <Card
      className={styles.leaf_card}
      withBorder
      shadow="sm"
      radius="md"
      onClick={onClick}
    >
      <Card.Section p="xs" className={styles.leaf_header}>
        <Group style={{ justifyContent: 'space-between' }}>
          <Text fw={600}>{leaf.name}</Text>
          <Badge variant="filled" color="blue" size="sm">
            {leaf.assetSymbol}
          </Badge>
        </Group>
      </Card.Section>

      <Stack style={{ gap: '0.5rem' }} p="md">
        <Flex justify="space-between" align="center">
          <Text size="sm" c="dimmed" fw={500}>
            Balance:
          </Text>
          <Text size="sm" fw={600}>
            {leaf.balance === 0 ? '0' : leaf.balance.toLocaleString()}
          </Text>
        </Flex>

        {leaf.usdEquivalent !== undefined && (
          <Flex justify="space-between" align="center">
            <Text size="sm" c="dimmed" fw={500}>
              USD Value:
            </Text>
            <Flex direction="column" align="flex-end">
              <Text size="sm" fw={700} c="blue">
                ${leaf.usdEquivalent.toLocaleString()}
              </Text>
              {leaf.balance > 0 && (
                <Text size="xs" c="dimmed">
                  ${(leaf.usdEquivalent / leaf.balance).toFixed(2)}
                </Text>
              )}
            </Flex>
          </Flex>
        )}
      </Stack>
    </Card>
  )
}
